import { useEffect, useMemo, useState } from 'react';

import { Box, Icon, Select, Screen, Text, TextInput } from '~/atomic';
import { formatToBRL } from '~/utils/formatFinance';

type PaymentFrequency = 'monthly' | 'semiannual' | 'annual' | 'bullet';
type InterestMode = 'pre' | 'pos';
type PostIndex = 'CDI' | 'SELIC' | 'IPCA';
type AmortizationMethod = 'price' | 'sac';
type ProductKey =
  | 'seguroPrestamista'
  | 'capitalizacao'
  | 'iof'
  | 'custasCartorarias'
  | 'avaliacaoGarantia'
  | 'projeto';
type ProductInput = { value: string; financed: boolean };

type ScheduleRow = {
  period: number;
  interest: number;
  principal: number;
  payment: number;
  remainingBalance: number;
};

type BcbSeries = {
  code: number;
  name: string;
  unit: string;
  source: string;
};

const FREQUENCY_OPTIONS: {
  value: PaymentFrequency;
  label: string;
  monthsPerInstallment: number;
  yearlyPeriods: number;
  hint: string;
}[] = [
  {
    value: 'monthly',
    label: 'Mensal',
    monthsPerInstallment: 1,
    yearlyPeriods: 12,
    hint: 'a cada 1 mês',
  },
  {
    value: 'semiannual',
    label: 'Semestral',
    monthsPerInstallment: 6,
    yearlyPeriods: 2,
    hint: 'a cada 6 meses',
  },
  {
    value: 'annual',
    label: 'Anual',
    monthsPerInstallment: 12,
    yearlyPeriods: 1,
    hint: 'a cada 12 meses',
  },
  {
    value: 'bullet',
    label: 'Pagamento final (Bullet)',
    monthsPerInstallment: 12,
    yearlyPeriods: 1,
    hint: 'uma única parcela no vencimento',
  },
];

const INTEREST_MODE_OPTIONS: { value: InterestMode; label: string }[] = [
  { value: 'pre', label: 'Pré' },
  { value: 'pos', label: 'Pós' },
];

const AMORTIZATION_OPTIONS: { value: AmortizationMethod; label: string }[] = [
  { value: 'price', label: 'Price (parcelas fixas)' },
  { value: 'sac', label: 'SAC (amortização constante)' },
];

const INDEX_OPTIONS: {
  value: PostIndex;
  label: string;
  code: number;
  codeSource: string;
}[] = [
  {
    value: 'CDI',
    label: 'CDI',
    code: 4389,
    codeSource:
      'https://www3.bcb.gov.br/sgspub/consultarvalores/consultarValoresSeries.do?hdOidSeriesSelecionadas=4389&method=consultarGraficoPorId',
  },
  {
    value: 'SELIC',
    label: 'SELIC',
    code: 1178,
    codeSource:
      'https://www.bcb.gov.br/controleinflacao/historicotaxasjuros',
  },
];

const BCB_API_URL_BY_CODE = (code: number) =>
  `https://api.bcb.gov.br/dados/serie/bcdata.sgs.${code}/dados/ultimos/2?formato=json`;

const csvCell = (value: string | number) =>
  `"${String(value).replace(/"/g, '""')}"`;

const formatCSVDate = (period: number, monthsPerInstallment: number) => {
  const start = new Date();
  const date = new Date(start.getFullYear(), start.getMonth() + period * monthsPerInstallment, 1);
  return `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`;
};

const toNumber = (value: string): number => {
  const trimmed = value.trim().replace(/\s/g, '');
  const normalized = trimmed.includes(',')
    ? trimmed.replace(/\./g, '').replace(',', '.')
    : trimmed;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : 0;
};

const toInteger = (value: string): number => {
  const parsed = Number(value.trim());
  return Number.isFinite(parsed) ? Math.max(0, Math.floor(parsed)) : 0;
};

function roundMoney(value: number): number {
  return Math.round(value);
}

function monthlyRateFromCashFlows(cashFlows: { month: number; amount: number }[]): number {
  if (cashFlows.length < 2) return 0;
  let rate = 0.01;
  for (let iteration = 0; iteration < 80; iteration += 1) {
    let value = 0;
    let derivative = 0;
    for (const cashFlow of cashFlows) {
      const denominator = Math.pow(1 + rate, cashFlow.month);
      value += cashFlow.amount / denominator;
      if (cashFlow.month > 0) {
        derivative -= (cashFlow.month * cashFlow.amount) /
          (Math.pow(1 + rate, cashFlow.month + 1));
      }
    }
    if (Math.abs(value) < 0.000001 || derivative === 0) return Math.max(rate, 0);
    const nextRate = rate - value / derivative;
    if (!Number.isFinite(nextRate) || nextRate <= -0.9999 || nextRate > 10) break;
    rate = nextRate;
  }
  return Math.max(rate, 0);
}

interface CalculatorProps {
  onSimulation?: () => Promise<boolean>;
}

export default function FinanciamentoCalculatorView({ onSimulation }: CalculatorProps) {
  const [principalInput, setPrincipalInput] = useState('100000');
  const [termInput, setTermInput] = useState('12');
  const [frequency, setFrequency] = useState<PaymentFrequency>('monthly');
  const [graceInput, setGraceInput] = useState('0');
  const [interestMode, setInterestMode] = useState<InterestMode>('pre');
  const [indexType, setIndexType] = useState<PostIndex>('CDI');
  const [taxaInput, setTaxaInput] = useState('10');
  const [amortizationMethod, setAmortizationMethod] =
    useState<AmortizationMethod>('price');
  const [productInputs, setProductInputs] = useState<Record<ProductKey, ProductInput>>({
    seguroPrestamista: { value: '0', financed: false },
    capitalizacao: { value: '0', financed: false },
    iof: { value: '0', financed: false },
    custasCartorarias: { value: '0', financed: false },
    avaliacaoGarantia: { value: '0', financed: false },
    projeto: { value: '0', financed: false },
  });

  const [indexData, setIndexData] = useState<Record<PostIndex, number>>({
    CDI: 0,
    SELIC: 0,
    IPCA: 0,
  });
  const [indexError, setIndexError] = useState('');
  const [indexLoading, setIndexLoading] = useState(false);
  const [simulationSubmitted, setSimulationSubmitted] = useState(false);

  const amount = toNumber(principalInput);
  const principalCents = roundMoney(amount * 100);
  const termMonths = toNumber(termInput);
  const graceMonths = Math.max(0, toInteger(graceInput));
  const productTotalsCents = useMemo(
    () =>
      Object.values(productInputs).reduce(
        (sum, product) => sum + roundMoney(toNumber(product.value) * 100),
        0,
      ),
    [productInputs],
  );
  const financedProductsCents = useMemo(
    () =>
      Object.values(productInputs).reduce(
        (sum, product) =>
          sum + (product.financed ? roundMoney(toNumber(product.value) * 100) : 0),
        0,
      ),
    [productInputs],
  );
  const upfrontProductsCents = productTotalsCents - financedProductsCents;
  const calculationPrincipalCents = principalCents + financedProductsCents;

  const selectedFrequency = useMemo(
    () => FREQUENCY_OPTIONS.find((option) => option.value === frequency),
    [frequency],
  );
  const selectedIndex = useMemo(
    () => INDEX_OPTIONS.find((option) => option.value === indexType),
    [indexType],
  );

  const installmentsTotal = useMemo(() => {
    if (!selectedFrequency || termMonths <= 0) return 0;
    if (frequency === 'bullet') return 1;
    return Math.max(
      1,
      Math.ceil(termMonths / selectedFrequency.monthsPerInstallment),
    );
  }, [selectedFrequency, termMonths, frequency]);

  const graceInstallments = useMemo(() => {
    if (graceMonths <= 0 || !selectedFrequency) return 0;
    return Math.min(
      installmentsTotal,
      Math.max(
        0,
        Math.floor(graceMonths / selectedFrequency.monthsPerInstallment),
      ),
    );
  }, [graceMonths, installmentsTotal, selectedFrequency]);

  const graceHintLabel = useMemo(() => {
    if (!selectedFrequency) return '';
    return `${graceMonths} meses = ${graceInstallments} ${
      graceInstallments === 1 ? 'parcela' : 'parcelas'
    } de carência`;
  }, [graceMonths, graceInstallments, selectedFrequency]);

  const spreadOrBaseRate = toNumber(taxaInput);
  const indexedBaseRate = useMemo(() => indexData[indexType] ?? 0, [indexData, indexType]);
  const effectiveAnnualRate = useMemo(() => {
    if (interestMode === 'pre') return spreadOrBaseRate;
    return indexedBaseRate + spreadOrBaseRate;
  }, [interestMode, indexedBaseRate, spreadOrBaseRate]);

  const periodicRate = useMemo(() => {
    if (!selectedFrequency) return 0;
    if (effectiveAnnualRate <= 0 || installmentsTotal <= 0) return 0;
    if (frequency === 'bullet') {
      return Math.pow(1 + effectiveAnnualRate / 100, termMonths / 12) - 1;
    }
    return (
      Math.pow(1 + effectiveAnnualRate / 100, 1 / selectedFrequency.yearlyPeriods) -
      1
    );
  }, [effectiveAnnualRate, selectedFrequency, installmentsTotal, frequency, termMonths]);

  const amortizedInstallments = Math.max(0, installmentsTotal - graceInstallments);
  const graceBalanceFinalRate = Math.pow(1 + periodicRate, graceInstallments);
  const amortizationBaseCents = roundMoney(
    calculationPrincipalCents * graceBalanceFinalRate,
  );

  const fixedAmortizedPayment = useMemo(() => {
    if (
      calculationPrincipalCents <= 0 ||
      installmentsTotal <= 0 ||
      amortizedInstallments <= 0 ||
      periodicRate <= 0
    ) {
      return 0;
    }
    return roundMoney(
      (amortizationBaseCents * periodicRate) /
        (1 - Math.pow(1 + periodicRate, -amortizedInstallments)),
    );
  }, [
    amortizedInstallments,
    amortizationBaseCents,
    periodicRate,
    calculationPrincipalCents,
    installmentsTotal,
  ]);

  const sacPrincipalPayment = useMemo(() => {
    if (amortizationBaseCents <= 0 || amortizedInstallments <= 0) return 0;
    return roundMoney(amortizationBaseCents / amortizedInstallments);
  }, [amortizationBaseCents, amortizedInstallments]);

  const schedule = useMemo<ScheduleRow[]>(() => {
    if (
      calculationPrincipalCents <= 0 ||
      installmentsTotal <= 0 ||
      periodicRate <= 0
    ) {
      return [];
    }

    const rows: ScheduleRow[] = [];
    let balance = calculationPrincipalCents;

    for (let index = 1; index <= installmentsTotal; index += 1) {
      const interest = roundMoney(balance * periodicRate);
      const isGrace = index <= graceInstallments;

      if (isGrace) {
        const payment = interest;
        balance = roundMoney(balance + payment);
        rows.push({
          period: index,
          interest,
          principal: 0,
          payment,
          remainingBalance: balance,
        });
        continue;
      }

      const remainingInstallments = installmentsTotal - index + 1;
      const isLastInstallment = remainingInstallments === 1;

      if (isLastInstallment) {
        const principal = balance;
        rows.push({
          period: index,
          interest,
          principal,
          payment: principal + interest,
          remainingBalance: 0,
        });
        balance = 0;
        continue;
      }

      let principal =
        amortizationMethod === 'sac'
          ? sacPrincipalPayment
          : fixedAmortizedPayment - interest;
      if (principal <= 0) {
        principal = 0;
      }
      if (principal > balance) {
        principal = balance;
      }

      const installment = principal + interest;

      balance = Math.max(0, balance - principal);
      rows.push({
        period: index,
        interest,
        principal,
        payment: installment,
        remainingBalance: balance,
      });
    }

    return rows;
  }, [
    installmentsTotal,
    fixedAmortizedPayment,
    graceInstallments,
    amortizationMethod,
    sacPrincipalPayment,
    calculationPrincipalCents,
    periodicRate,
  ]);

  const totals = useMemo(() => {
    const paidInterest = schedule.reduce((sum, row) => sum + row.interest, 0);
    const paidPrincipal = schedule.reduce((sum, row) => sum + row.principal, 0);
    const totalPaid = paidInterest + paidPrincipal;
    return {
      interestCents: paidInterest,
      principalCents: paidPrincipal,
      totalCents: totalPaid,
    };
  }, [schedule]);

  const productsAnnualRate = useMemo(() => {
    if (amount <= 0 || termMonths <= 0 || productTotalsCents <= 0) return 0;
    return (productTotalsCents / principalCents) * (12 / termMonths) * 100;
  }, [amount, principalCents, productTotalsCents, termMonths]);
  const combinedAnnualRate = effectiveAnnualRate + productsAnnualRate;

  useEffect(() => {
    if (interestMode !== 'pos') {
      setIndexError('');
      return;
    }

    let cancelled = false;
    const indexEntry = INDEX_OPTIONS.find((item) => item.value === indexType);
    if (!indexEntry) return;

    const fetchIndexValue = async () => {
      setIndexLoading(true);
      setIndexError('');
      try {
        const response = await fetch(BCB_API_URL_BY_CODE(indexEntry.code));
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        const payload = (await response.json()) as Array<{
          data: string;
          valor: string;
        }>;
        const latest = payload.at(-1);
        if (!latest) {
          throw new Error('Resposta vazia da API do Banco Central');
        }
        const numeric = toNumber(latest.valor);
        // SGS 4389 (CDI) and 1178 (effective Selic) already report annualized
        // rates on a 252-business-day basis. Do not annualize them again.
        const annualRate = numeric;

        if (!cancelled) {
          setIndexData((current) => ({
            ...current,
            [indexType]: Math.max(annualRate, 0),
          }));
        }
      } catch (error) {
        if (!cancelled) {
          setIndexError(
            'Não foi possível atualizar a taxa de referência do Banco Central agora.',
          );
          setIndexData((current) => ({
            ...current,
            [indexType]: 0,
          }));
        }
      } finally {
        if (!cancelled) setIndexLoading(false);
      }
    };

    void fetchIndexValue();

    return () => {
      cancelled = true;
    };
  }, [indexType, interestMode]);

  useEffect(() => {
    if (interestMode === 'pre') return;
    setIndexLoading(true);
    setIndexError('');
    setIndexData((current) => ({
      ...current,
      [indexType]: 0,
    }));
  }, [interestMode, indexType]);

  const indexSeriesInfo: BcbSeries[] = [
    {
      code: 1178,
      name: 'SELIC',
      unit: '% a.a. (base 252 dias úteis)',
      source:
        'https://www.bcb.gov.br/controleinflacao/historicotaxasjuros',
    },
    {
      code: 4389,
      name: 'CDI',
      unit: '% a.a. (base 252 dias úteis)',
      source:
        'https://www3.bcb.gov.br/sgspub/consultarvalores/consultarValoresSeries.do?hdOidSeriesSelecionadas=4389&method=consultarGraficoPorId',
    },
  ];

  const indexedRateLabel = `Taxa de referência atual (${indexType}): ${
    indexedBaseRate > 0 ? `${indexedBaseRate.toFixed(4)}% a.a.` : '—'
  }`;

  const validationUrl =
    typeof window === 'undefined'
      ? ''
      : `${window.location.origin}/financeiro/calculadora`;

  const csvContent = useMemo(() => {
    if (!schedule.length || !selectedFrequency || amount <= 0 || installmentsTotal <= 0) {
      return '';
    }

    const summaryRows = [
      ['Cenário', ''],
      ['Valor', formatToBRL(amount.toString())],
      ['Prazo (meses)', termMonths],
      ['Forma de pagamento', selectedFrequency.label],
      ['Carência (meses)', graceMonths],
      ['Taxa', `${interestMode === 'pre' ? 'Pré' : 'Pós'} (${taxaInput})`],
      ['Sistema de amortização', amortizationMethod === 'sac' ? 'SAC' : 'Price'],
      [
        'Índice base',
        interestMode === 'pre'
          ? 'Não se aplica'
          : `${indexType} (${selectedIndex?.label ?? selectedIndex?.value})`,
      ],
      [
        'Taxa efetiva a.a.',
        `${effectiveAnnualRate.toFixed(6)}%`,
      ],
      ['Total de juros', formatToBRL((totals.interestCents / 100).toString())],
      ['Total a pagar', formatToBRL((totals.totalCents / 100).toString())],
      ['Produtos vinculados', formatToBRL((productTotalsCents / 100).toString())],
      ['CET do contrato', `${effectiveAnnualRate.toFixed(6)}% a.a.`],
      ['Taxa média anual dos produtos', `${productsAnnualRate.toFixed(6)}% a.a.`],
      ['Taxa contrato + produtos', `${combinedAnnualRate.toFixed(6)}% a.a.`],
      [
        'Custo total final com produtos',
        formatToBRL(((totals.totalCents + upfrontProductsCents) / 100).toString()),
      ],
      ['Link de validação', validationUrl],
    ]
      .map((row) => `${csvCell(row[0])};${csvCell(row[1])}`)
      .join('\n');

    const header = [
      'Parcela',
      'Data prevista',
      'Juros',
      'Amortização',
      'Parcela',
      'Saldo',
    ].map(csvCell);

    const body = schedule
      .map(
        (row) =>
          `${csvCell(row.period)};${csvCell(formatCSVDate(row.period, selectedFrequency.monthsPerInstallment))};${csvCell((row.interest / 100).toFixed(2))};${csvCell((row.principal / 100).toFixed(2))};${csvCell((row.payment / 100).toFixed(2))};${csvCell((row.remainingBalance / 100).toFixed(2))}`,
      )
      .join('\n');

    return [`"Cronograma de Financiamento"`, summaryRows, '', `${header.join(';')}`, body]
      .filter(Boolean)
      .join('\n');
  }, [
    amount,
    graceMonths,
    selectedFrequency,
    schedule,
    termMonths,
    interestMode,
    selectedIndex,
    effectiveAnnualRate,
    totals.interestCents,
    totals.totalCents,
    productTotalsCents,
    upfrontProductsCents,
    productsAnnualRate,
    combinedAnnualRate,
    validationUrl,
    taxaInput,
    amortizationMethod,
    indexType,
    installmentsTotal,
    selectedFrequency?.monthsPerInstallment,
  ]);

  const handleExportCSV = () => {
    if (!csvContent) return;
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const href = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = href;
    link.download = `simulacao-financiamento-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(href);
  };

  const handleExportPDF = () => {
    if (!schedule.length) return;
    window.print();
  };

  const handleSimulation = async () => {
    if (!onSimulation || simulationSubmitted) return;
    const accepted = await onSimulation();
    if (accepted) setSimulationSubmitted(true);
  };

  return (
    <Screen
      headerTitle="Calculadora de financiamento"
      hasPadding
      bgClassName="bg-[#F3F7F1]"
    >
      <div className="flex flex-col gap-medium py-medium">
        <div
          className="relative overflow-hidden rounded-large px-medium py-large shadow-level2 sm:px-large"
          style={{ backgroundColor: '#FFFFFF', border: '1px solid #D6E2D8' }}
        >
          <div className="absolute -right-12 -top-16 h-48 w-48 rounded-full bg-[#EAF3E8]" />
          <div className="absolute -bottom-20 right-24 h-40 w-40 rounded-full border-[18px] border-[#EAF3E8]" />
          <div className="relative flex flex-col gap-small sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-small">
              <div className="flex h-[52px] w-[52px] shrink-0 items-center justify-center overflow-hidden rounded-medium">
                <img
                  src="/logo.png"
                  alt="Paga Agro"
                  className="h-full w-full object-contain mix-blend-multiply"
                />
              </div>
              <div>
                <Text className="text-captionMD font-bold uppercase tracking-[0.16em] text-[#174B35]">
                  Paga Agro
                </Text>
                <Text className="mt-quark text-headingH2 font-bold text-[#173629]">
                  Crédito rural com clareza
                </Text>
              </div>
            </div>
            <span className="w-fit rounded-full border border-[#B9D1BE] bg-[#F3F8F2] px-small py-quark text-captionMD font-bold tracking-[0.08em] text-[#174B35]">
              SIMULAÇÃO
            </span>
          </div>
          <Text className="relative mt-medium max-w-[620px] text-bodyMD leading-relaxed text-[#405248]">
            Compare cenários de financiamento e encontre a estrutura que faz sentido
            para sua operação no campo.
          </Text>
        </div>

        <Text className="max-w-[720px] text-bodyMD leading-relaxed color-complement-01">
          Simulador com a mecânica usada em instituições financeiras para parcelas
          fixas com juros compostos, com opção de carência e taxa pré ou pós.
        </Text>

        <Box className="rounded-medium border border-[#D6E2D8] bg-white p-small shadow-sm flex flex-col gap-small">
          <Text className="text-headingH3 font-bold color-complement-01">Dados da operação</Text>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-small">
            <TextInput
              label="Valor (R$)"
              value={principalInput}
              placeholder="100.000,00"
              keyboardType="numeric"
              inputMode="decimal"
              onChangeText={setPrincipalInput}
              mask="money"
            />

            <TextInput
              label="Prazo (em meses)"
              value={termInput}
              placeholder="12"
              keyboardType="numeric"
              inputMode="numeric"
              onChangeText={setTermInput}
              mask="integer"
            />
          </div>

          {installmentsTotal > 1 ? (
            <Select
              label="Sistema de amortização"
              options={AMORTIZATION_OPTIONS}
              value={amortizationMethod}
              onChange={(value) => setAmortizationMethod(value)}
            />
          ) : null}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-small">
            <Select
              label="Forma de pagamento"
              options={FREQUENCY_OPTIONS.map((item) => ({
                value: item.value,
                label: `${item.label} (${item.hint})`,
              }))}
              value={frequency}
              onChange={(value) => setFrequency(value)}
            />

            <TextInput
              label="Carência (meses)"
              value={graceInput}
              placeholder="0"
              keyboardType="numeric"
              inputMode="numeric"
              onChangeText={setGraceInput}
              mask="integer"
            />
            <Text className="text-bodySM color-intense-06">
              {graceHintLabel || 'Sem carência.'}
            </Text>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-small">
            <Select
              label="Tipo de taxa"
              options={INTEREST_MODE_OPTIONS}
              value={interestMode}
              onChange={(value) => setInterestMode(value)}
            />
            <TextInput
              label="Taxa (% a.a.)"
              value={taxaInput}
              placeholder={
                interestMode === 'pre'
                  ? 'Taxa fixa anual'
                  : 'Spread sobre o índice'
              }
              keyboardType="numeric"
              inputMode="decimal"
              onChangeText={setTaxaInput}
              mask="percent"
            />
            <Text className="text-bodySM color-intense-06">
              {interestMode === 'pre'
                ? 'Ex.: 14 para 14% a.a. (pré-fixado)'
                : 'Ex.: 1,20 para 1,20% acima do índice escolhido'}
            </Text>
          </div>

          {interestMode === 'pos' ? (
            <Select
              label="Taxa pós-fixada indexada a"
              options={INDEX_OPTIONS.map((item) => ({
                value: item.value,
                label: `${item.label} (SGS ${item.code})`,
              }))}
              value={indexType}
              onChange={(value) => setIndexType(value)}
              disabled={interestMode !== 'pos'}
              className=''
            />
          ) : null}

          <div className="rounded-small bg-intense-02 border border-intense-03 p-small">
            <Text className="text-bodySM font-semibold color-complement-01">Fonte oficial usada</Text>
            <Text className="mt-quark text-bodyMD color-complement-01">
              {indexedRateLabel}
            </Text>
            {indexLoading ? (
              <Text className="text-bodySM color-intense-06 mt-quark">
                Atualizando taxa do BC...
              </Text>
            ) : null}
            {indexError ? (
              <Text className="text-captionMD color-warning-04 mt-quark">
                {indexError}
              </Text>
            ) : null}
            {selectedIndex ? (
              <a
                href={selectedIndex.codeSource}
                target="_blank"
                rel="noreferrer"
                className="text-captionMD color-primary-01 inline-block mt-quark underline"
              >
                Ver a série oficial no Portal de Dados Abertos ({selectedIndex.value}
                / código SGS {selectedIndex.code})
              </a>
            ) : null}
          </div>

          <div className="rounded-small border border-[#D6E2D8] bg-[#F8FBF7] p-small flex flex-col gap-small">
            <div>
              <Text className="text-bodySM font-semibold color-complement-01">
                Produtos vinculados ao projeto
              </Text>
              <Text className="mt-quark text-bodySM color-intense-06">
                Informe os valores que serão contratados junto com o financiamento. Eles ficam separados do principal do contrato.
              </Text>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-small">
              {([
      ['seguroPrestamista', 'Seguro Prestamista'],
      ['capitalizacao', 'Capitalização'],
                ['iof', 'IOF da operação'],
                ['custasCartorarias', 'Custas Cartorárias'],
                ['avaliacaoGarantia', 'Avaliação de garantia'],
                ['projeto', 'Projeto'],
              ] as const).map(([key, label]) => (
                <div key={key} className="flex flex-col gap-quark">
                <TextInput
                  key={key}
                  label={`${label} (R$)`}
                  value={productInputs[key].value}
                  placeholder="0,00"
                  keyboardType="numeric"
                  inputMode="decimal"
                  onChangeText={(value) =>
                    setProductInputs((current) => ({
                      ...current,
                      [key]: { ...current[key], value },
                    }))
                  }
                  mask="money"
                />
                <Select
                  label="Será financiado?"
                  options={[
                    { value: 'no', label: 'Não — pago na contratação' },
                    { value: 'yes', label: 'Sim — entra nas parcelas' },
                  ]}
                  value={productInputs[key].financed ? 'yes' : 'no'}
                  onChange={(value) =>
                    setProductInputs((current) => ({
                      ...current,
                      [key]: { ...current[key], financed: value === 'yes' },
                    }))
                  }
                />
                </div>
              ))}
            </div>
            <div className="rounded-small bg-white border border-intense-03 p-small">
              <Text className="text-bodySM color-intense-06">Total dos produtos</Text>
              <Text className="mt-nano text-bodyLG color-complement-01">
                {formatToBRL((productTotalsCents / 100).toString())}
              </Text>
              <Text className="mt-quark text-bodySM color-intense-06">
                Financiados: {formatToBRL((financedProductsCents / 100).toString())} ·
                Pagos na contratação: {formatToBRL((upfrontProductsCents / 100).toString())}
              </Text>
            </div>
          </div>
        </Box>

        <Box className="rounded-medium border border-[#D6E2D8] bg-white p-small shadow-sm flex flex-col gap-small">
          <Text className="text-headingH3 font-bold color-complement-01">Resumo da simulação</Text>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-small">
            <div className="rounded-small bg-intense-02 border border-intense-03 p-small">
              <Text className="text-bodySM font-semibold color-intense-06">Capital</Text>
              <Text className="mt-nano text-bodyLG color-complement-01">
                {formatToBRL(amount.toString())}
              </Text>
            </div>
            <div className="rounded-small bg-intense-02 border border-intense-03 p-small">
              <Text className="text-bodySM font-semibold color-intense-06">CET do contrato</Text>
              <Text className="mt-nano text-bodyLG color-complement-01">
                {effectiveAnnualRate.toFixed(4)}%
              </Text>
              <Text className="text-bodySM color-intense-06">a.a.</Text>
              <Text className="text-captionMD color-intense-06">
                taxa anual equivalente
              </Text>
            </div>
            <div className="rounded-small bg-intense-02 border border-intense-03 p-small">
              <Text className="text-bodySM font-semibold color-intense-06">Parcelas totais</Text>
              <Text className="mt-nano text-bodyLG color-complement-01">
                {installmentsTotal}
              </Text>
              <Text className="text-bodySM color-intense-06">
                {selectedFrequency?.hint}
              </Text>
            </div>
            <div className="rounded-small bg-[#EEF7EE] border border-[#CFE3D1] p-small">
              <Text className="text-bodySM font-semibold color-intense-06">Taxa média dos produtos</Text>
              <Text className="mt-nano text-bodyLG color-complement-01">
                {productsAnnualRate.toFixed(4)}%
              </Text>
              <Text className="text-bodySM color-intense-06">a.a.</Text>
              <Text className="text-captionMD color-intense-06">
                taxa anual equivalente
              </Text>
            </div>
            <div className="rounded-small bg-[#E7F2E8] border border-[#BFD8C2] p-small">
              <Text className="text-bodySM font-semibold color-intense-06">
                Taxa contrato + produtos
              </Text>
              <Text className="mt-nano text-bodyLG color-complement-01">
                {combinedAnnualRate.toFixed(4)}%
              </Text>
              <Text className="text-bodySM color-intense-06">a.a.</Text>
            </div>
            <div className="rounded-small bg-intense-02 border border-intense-03 p-small">
              <Text className="text-bodySM font-semibold color-intense-06">Carência</Text>
              <Text className="mt-nano text-bodyLG color-complement-01">
                {graceInstallments} {graceInstallments === 1 ? 'parcela' : 'parcelas'}
              </Text>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-small">
            <div className="rounded-small bg-support-01 border border-intense-03 p-small">
              <Text className="text-captionMD color-complement-01">Total em juros</Text>
              <Text className="mt-nano text-bodyLG color-complement-01">
                {formatToBRL((totals.interestCents / 100).toString())}
              </Text>
            </div>
            <div className="rounded-small bg-support-01 border border-intense-03 p-small">
              <Text className="text-captionMD color-complement-01">Total a pagar</Text>
              <Text className="mt-nano text-bodyLG color-complement-01">
                {formatToBRL((totals.totalCents / 100).toString())}
              </Text>
            </div>
            <div className="rounded-small bg-support-01 border border-intense-03 p-small">
              <Text className="text-captionMD color-complement-01">Amortização total</Text>
              <Text className="mt-nano text-bodyLG color-complement-01">
                {formatToBRL((totals.principalCents / 100).toString())}
              </Text>
            </div>
            <div className="rounded-small bg-support-01 border border-intense-03 p-small">
              <Text className="text-captionMD color-complement-01">
                Custo total final com produtos
              </Text>
              <Text className="mt-nano text-bodyLG color-complement-01">
                {formatToBRL(
                  ((totals.totalCents + upfrontProductsCents) / 100).toString(),
                )}
              </Text>
              <Text className="text-bodySM color-intense-06">
                parcelas + produtos pagos na contratação
              </Text>
            </div>
          </div>

          <Text className="rounded-small border border-intense-03 bg-intense-02 p-small text-bodySM color-intense-06">
            Como o resultado foi calculado: o CET do contrato considera o fluxo das
            parcelas do financiamento. A demonstração “taxa contrato + produtos” soma
            a taxa anual do contrato à taxa média anual dos produtos: valor total dos
            produtos ÷ principal ÷ prazo em anos. Essa soma é demonstrativa e não
            substitui o CET regulatório calculado pela instituição financeira.
          </Text>

          <button
            type="button"
            onClick={handleExportPDF}
            disabled={!schedule.length || (onSimulation ? !simulationSubmitted : false)}
            className={`mt-small inline-flex items-center justify-center h-[48px] px-small rounded-small ${
              schedule.length && (!onSimulation || simulationSubmitted) ? 'bg-primary-01 color-intense-01' : 'bg-intense-03'
            }`}
          >
            <Text className="text-bodyMD color-intense-01">
              Exportar cronograma (PDF)
            </Text>
          </button>

          {onSimulation ? (
            <button
              type="button"
              onClick={() => void handleSimulation()}
              disabled={!csvContent || simulationSubmitted}
              className={`inline-flex items-center justify-center h-[48px] px-small rounded-small border border-primary-01 ${
                csvContent && !simulationSubmitted
                  ? 'color-primary-01 hover:bg-support-01'
                  : 'color-intense-05'
              }`}
            >
              {simulationSubmitted ? 'Simulação registrada' : 'Confirmar simulação'}
            </button>
          ) : null}

          <Text className="text-bodySM color-intense-06">
            Esta simulação considera juros compostos e o sistema de amortização{' '}
            {amortizationMethod === 'sac' ? 'SAC' : 'Price'}
            {graceInstallments > 0
              ? ` com ${graceInstallments} período${
                  graceInstallments === 1 ? '' : 's'
                } de carência (juros capitalizados).`
              : '.'}
          </Text>
        </Box>

        <Box className="rounded-medium border border-[#D6E2D8] bg-white p-small shadow-sm flex flex-col gap-small">
          <Text className="text-headingH3 font-bold color-complement-01">Cronograma de parcelas</Text>
          {onSimulation && !simulationSubmitted ? (
            <Text className="text-bodyMD color-intense-06">
              Revise os dados acima e confirme a simulação para visualizar as parcelas.
            </Text>
          ) : schedule.length === 0 ? (
            <Text className="text-bodyMD color-intense-06">
              Informe o valor e o prazo para gerar o cronograma.
            </Text>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-[680px] w-full text-left">
                <thead>
                  <tr className="border-b border-intense-03">
                    <th className="py-small pr-small text-captionMD color-intense-06">
                      Parcela
                    </th>
                    <th className="py-small pr-small text-captionMD color-intense-06">
                      Juros
                    </th>
                    <th className="py-small pr-small text-captionMD color-intense-06">
                      Principal
                    </th>
                    <th className="py-small pr-small text-captionMD color-intense-06">
                      Parcela
                    </th>
                    <th className="py-small text-captionMD color-intense-06">Saldo</th>
                  </tr>
                </thead>
                <tbody>
                  {schedule.map((row) => (
                    <tr key={row.period} className="border-b border-intense-04">
                      <td className="py-small pr-small text-bodyMD color-complement-01">
                        {row.period}
                      </td>
                      <td className="py-small pr-small text-bodyMD color-complement-01">
                        {formatToBRL((row.interest / 100).toString())}
                      </td>
                      <td className="py-small pr-small text-bodyMD color-complement-01">
                        {formatToBRL((row.principal / 100).toString())}
                      </td>
                      <td className="py-small pr-small text-bodyMD color-complement-01">
                        {formatToBRL((row.payment / 100).toString())}
                      </td>
                      <td className="py-small text-bodyMD color-complement-01">
                        {formatToBRL((row.remainingBalance / 100).toString())}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Box>

        <Box className="rounded-medium border border-[#D6E2D8] bg-white p-small shadow-sm flex flex-col gap-small">
          <Text className="text-headingH3 font-bold color-complement-01">
            Fontes oficiais de validação
          </Text>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-small">
            {indexSeriesInfo.map((series) => (
              <a
                key={series.name}
                href={series.source}
                target="_blank"
                rel="noreferrer"
                className="rounded-small bg-intense-02 border border-intense-03 p-small hover:border-primary-01 transition-colors"
              >
                <Text className="text-bodyMD color-complement-01">
                  {series.name} (código SGS {series.code})
                </Text>
                <Text className="text-bodySM color-intense-06 mt-quark">
                  {series.unit}
                </Text>
              </a>
            ))}
          </div>
          <Text className="text-bodySM color-intense-06">
            Observação: API pública do BCB para consulta de séries
            (`api.bcb.gov.br/dados/serie/bcdata.sgs.{'{'}código{'}'}/dados`).
          </Text>
        </Box>

        <Box className="rounded-small bg-primary-01/10 border border-primary-01/30 px-small py-middle flex flex-col gap-quark">
          <Text className="text-bodyMD color-complement-01">
            Link de validação para teste:
            {validationUrl ? (
              <a
                href={validationUrl}
                target="_blank"
                rel="noreferrer"
                className="font-bold text-bodyMD color-primary-01 underline block mt-quark"
              >
                {validationUrl}
              </a>
            ) : null}
          </Text>
        </Box>

        <div className="pb-medium text-bodySM color-intense-06 flex items-start gap-nano">
          <Icon name="icon-circle-alert" size={14} color="#A0A0A0" />
          <Text as="p">
            Esta é uma simulação educacional. Para operação real, confirme custos
            totais e componentes financeiros com a instituição (tarifas, IOF, seguro,
            registro e comissões).
          </Text>
        </div>
      </div>
    </Screen>
  );
}
